import { Form, Formik, FormikHelpers } from "formik";
import React, { useContext, useEffect, useState } from "react";
import { useMessageSource } from "react-message-source";
import ApplicationPageContent from "../../../ui/layout/page/content/application";
import ContentFooter from "../../../ui/layout/page/content/footer";
import NavigationBar from "../../../ui/layout/page/content/footer/navigation-bar";
import FormContainer from "../../../ui/layout/page/forms-container/form-base-container";
import InputContainer from "../../../ui/layout/page/forms-container/form-item-container/input-container";
import SectionFullWidthContainer from "../../../ui/layout/page/forms-container/section-full-width-container";
import SectionItemContainer from "../../../ui/layout/page/forms-container/section-item-container";
import FlowHeader from "../../../ui/widgets/flow-header";
import CustomLink from "../../../ui/widgets/link";
import Typography from "../../../ui/widgets/typography";
import { displayErrorInField } from "../../../util/formik";
import { AdminModel } from "../types";
import TextInputField from "../../shared/components/formik/text-input";
import ProductImage from "../../shared/components/product-image";
import useEmailOtpValidationSchema from "./validator";
import { OnEmailOtpRequiredApi } from "iam-client/dist/wrapper/registrationFlow";
import { LanguageContext } from "../../../context/languageContext";
import GridContainerItem from "../../../ui/layout/page/container-item";

import logging from "../../../util/logging";
import InvalidFormError, {
  isSubmitErrorMessageShown,
} from "../../../ui/layout/page/content/footer/form-error/invalid-form-error";
import { CancelApplicationContext } from "../../../context/cancelApplicationContext";
import { ProductContext } from "../../../context/product-context/productContext";
import { LinkEventHandler } from "../../../ui/widgets/types.tsx";
const log = logging("email otp page");

export type EmailOtpProps = {
  context: {
    companyName: string;
  };
  admin: AdminModel;
  submitEmailOtp: OnEmailOtpRequiredApi;
  formErrors: unknown;
  previous: () => void;
};

type FormData = {
  emailOtp: string;
};

const EmailOtp = ({
  context,
  admin,
  submitEmailOtp,
  formErrors,
  previous,
}: EmailOtpProps): React.JSX.Element => {
  const { getMessage, getMessageWithNamedParams } = useMessageSource();

  const [formActions, setFormActions] = useState<FormikHelpers<FormData>>();
  const { setCancelDialogActive } = useContext(CancelApplicationContext);

  useEffect(() => {
    // email top screen for some reason when opened focuses on the bottom of the page (footer).
    // this fixes that problem.
    window.scrollTo(0, 0);
  }, []);

  const handleChange: LinkEventHandler = (e) => {
    e.preventDefault();
    previous();
  };

  const defaultData = {
    emailOtp: "",
  };

  if (formErrors && formActions) {
    displayErrorInField(
      formActions,
      "emailOtp",
      getMessage("EmailOtp.ErrorOnAirlock"),
    );
  }

  const submit = (values: FormData, formikActions: FormikHelpers<FormData>) => {
    if (submitEmailOtp) {
      submitEmailOtp.proceed({
        otp: values.emailOtp,
      });
    } else {
      log.error(
        "Registration flow: Cannot proceed in submit email OTP, due to a missing callback",
      );
    }
    setFormActions(formikActions);
  };

  const { language } = useContext(LanguageContext);
  const { selectedProduct } = useContext(ProductContext);
  return (
    <>
      <FlowHeader
        title={getMessage("FlowHeader.basicAccountApplication")}
        image={<ProductImage product={selectedProduct?.productRef} />}
        subTitle={context.companyName}
      />
      <ApplicationPageContent>
        <SectionFullWidthContainer>
          <SectionItemContainer>
            <Typography variant="h3">
              {getMessage("EmailAddressVerification")}
            </Typography>
            <Typography>
              {getMessageWithNamedParams(
                "EmailAddressVerification-description",
                {
                  email: admin.emailBusiness,
                },
              )}
            </Typography>
          </SectionItemContainer>
        </SectionFullWidthContainer>
        <Formik
          key={language}
          validationSchema={useEmailOtpValidationSchema()}
          initialValues={defaultData}
          onSubmit={async (values, formikActions) => {
            await submit(values, formikActions);
            formikActions.setSubmitting(false);
          }}
        >
          {({ handleSubmit, isSubmitting, submitCount, errors }) => (
            <Form>
              <FormContainer>
                <InputContainer>
                  <TextInputField
                    name="emailOtp"
                    type="tel"
                    label={getMessage("EmailAddressVerification.code")}
                  />
                </InputContainer>
              </FormContainer>
              <GridContainerItem>
                <Typography variant="body1">
                  {getMessage("EmailAddressVerification.wrong-email-text")}
                </Typography>
              </GridContainerItem>
              <SectionItemContainer>
                <CustomLink href="#" onClick={handleChange}>
                  {getMessage("ConfirmationContactPersonDataChange")}
                </CustomLink>
              </SectionItemContainer>
              <ContentFooter>
                <InvalidFormError
                  isDisplayed={isSubmitErrorMessageShown(submitCount, errors)}
                />
                <NavigationBar
                  onCancel={() => setCancelDialogActive(true)}
                  cancelButtonText={getMessage("Onboarding.cancel")}
                  isSubmitting={isSubmitting}
                  backButtonText={getMessage("Onboarding.back")}
                  nextButtonText={getMessage("Onboarding.continue")}
                  onBack={previous}
                  onNext={handleSubmit}
                />
              </ContentFooter>
            </Form>
          )}
        </Formik>
      </ApplicationPageContent>
    </>
  );
};

export default EmailOtp;
